import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Calendar, LogOut, Shield, UtensilsCrossed, Wine, FileText, Copy, Mail, AlertTriangle, XCircle, Download, History, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_comunelaval-halls/artifacts/khvga6ci_Cuns%C3%ABi%20de%20Comun%2020251017_124442.jpg";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [halls, setHalls] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [announcement, setAnnouncement] = useState(null);
  const [categories, setCategories] = useState([]);
  const [adminEmail, setAdminEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [hallsRes, bookingsRes, announcementRes, categoriesRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/halls`),
        axios.get(`${API_URL}/bookings`),
        axios.get(`${API_URL}/announcement`),
        axios.get(`${API_URL}/categories`),
        axios.get(`${API_URL}/admin-settings`)
      ]);
      setHalls(hallsRes.data);
      setBookings(bookingsRes.data);
      setAnnouncement(announcementRes.data);
      setCategories(categoriesRes.data.categories || []);
      setAdminEmail(settingsRes.data.cancellation_email || '');
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const now = new Date();
  const { upcoming, past } = useMemo(() => {
    const up = [];
    const pa = [];
    bookings.forEach(b => {
      if (b.end_datetime) {
        const end = new Date(b.end_datetime);
        if (end >= now) up.push(b);
        else pa.push(b);
      } else {
        up.push(b);
      }
    });
    return { upcoming: up, past: pa.reverse() };
  }, [bookings]);

  const getCategoryLabel = (tipo) => {
    if (tipo === 'comune_scuole') return 'Comune/Scuole';
    if (tipo === 'anziani') return 'Anziani';
    if (tipo === 'solo_cucina') return 'Solo Cucina';
    const cat = categories.find(c => c.key === tipo);
    return cat?.label || tipo?.replace(/_/g, ' ') || 'N/A';
  };

  const getCategoryShortLabel = (tipo) => {
    if (tipo === 'comune_scuole') return 'Comune/Scuole';
    if (tipo === 'anziani') return 'Anziani';
    if (tipo === 'solo_cucina') return 'Solo Cucina';
    const cat = categories.find(c => c.key === tipo);
    if (cat?.label) {
      const i = cat.label.indexOf(':');
      return i > -1 ? cat.label.substring(0, i).trim() : cat.label;
    }
    return tipo?.replace(/_/g, ' ') || 'N/A';
  };

  const TIER_LABELS = { "1": "Ass. pubbl. util. residenti", "2": "Ass. non res. / Pers. residenti", "3": "Non residenti" };
  const getTierLabel = (b) => b.tipo_prezzo ? (TIER_LABELS[b.tipo_prezzo] || b.tipo_prezzo) : 'N/A';

  const generateReceiptText = (booking) => {
    const startDate = booking.start_datetime ? new Date(booking.start_datetime).toLocaleString('it-IT') : 'N/A';
    const endDate = booking.end_datetime ? new Date(booking.end_datetime).toLocaleString('it-IT') : 'N/A';
    return `\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550
    RICEVUTA PRENOTAZIONE
    Comune di La Val - Salf manifestaziuns
\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550

Codice: ${booking.id.substring(0, 8).toUpperCase()}
Data Creazione: ${new Date(booking.created_at).toLocaleDateString('it-IT')}

SALA: ${booking.hall_info?.name || 'N/A'}

PERIODO:
Dal: ${startDate}
Al:  ${endDate}

DESCRIZIONE: ${booking.descrizione_manifestazione || ''}

CATEGORIA: ${getCategoryLabel(booking.tipo_manifestazione)}
TIPO RICHIEDENTE: ${getTierLabel(booking)}

SERVIZI: ${booking.use_cucina ? 'Cucina' : 'Nessuno'}

RESPONSABILE:
Nome: ${booking.user_info?.nome_cognome || 'N/A'}
Email: ${booking.user_info?.email || 'N/A'}
${booking.user_info?.societa_associazione ? 'Societ\u00e0: ' + booking.user_info.societa_associazione : ''}

\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
TOTALE: \u20ac ${booking.total_price.toFixed(2)}
\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550`.trim();
  };

  const handleDownloadPDF = async (bookingId) => {
    try {
      const res = await axios.get(`${API_URL}/bookings/${bookingId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `prenotazione_${bookingId.substring(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF scaricato!');
    } catch (error) {
      toast.error('Errore nel download del PDF');
    }
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Caricamento...</div>;

  const BookingCard = ({ booking, isPast }) => (
    <Card className={`card-hover ${isPast ? 'opacity-75' : ''}`} data-testid={`booking-${booking.id}`}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="font-heading text-xl">{booking.hall_info?.name}</CardTitle>
            {booking.user_info?.societa_associazione && (
              <p className="text-sm font-medium text-primary mt-1">{booking.user_info.societa_associazione}</p>
            )}
            <CardDescription>
              {booking.start_datetime && booking.end_datetime
                ? `${new Date(booking.start_datetime).toLocaleDateString('it-IT')} ${new Date(booking.start_datetime).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})} - ${new Date(booking.end_datetime).toLocaleDateString('it-IT')} ${new Date(booking.end_datetime).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})}`
                : 'Date non disponibili'}
            </CardDescription>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Badge variant="outline" className="text-lg font-semibold">&euro; {booking.total_price.toFixed(2)}</Badge>
            {isPast && <Badge variant="secondary" className="text-xs">Passata</Badge>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm">{booking.descrizione_manifestazione}</p>
        <div className="flex flex-wrap gap-2">
          {booking.use_cucina && <Badge>Cucina</Badge>}
          <Badge variant="secondary">{getTierLabel(booking)}</Badge>
          <Badge variant="outline">{getCategoryShortLabel(booking.tipo_manifestazione)}</Badge>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => handleDownloadPDF(booking.id)} data-testid={`download-pdf-${booking.id}`}>
            <Download className="w-4 h-4 mr-2" /> Scarica PDF
          </Button>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid={`view-receipt-${booking.id}`}>
                <FileText className="w-4 h-4 mr-2" /> Ricevuta
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Ricevuta Prenotazione</DialogTitle>
                <DialogDescription>Codice: {booking.id.substring(0, 8).toUpperCase()}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <pre className="bg-muted p-4 rounded-lg text-xs sm:text-sm font-mono whitespace-pre-wrap overflow-auto">{generateReceiptText(booking)}</pre>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button onClick={() => { navigator.clipboard.writeText(generateReceiptText(booking)); toast.success('Ricevuta copiata!'); }} className="flex-1">
                    <Copy className="w-4 h-4 mr-2" /> Copia Ricevuta
                  </Button>
                  <Button variant="outline" onClick={() => handleDownloadPDF(booking.id)} className="flex-1">
                    <Download className="w-4 h-4 mr-2" /> Scarica PDF
                  </Button>
                  <Button variant="outline" onClick={() => {
                    const text = generateReceiptText(booking);
                    window.location.href = `mailto:?subject=${encodeURIComponent(`Ricevuta Prenotazione ${booking.id.substring(0, 8).toUpperCase()}`)}&body=${encodeURIComponent(text)}`;
                  }} className="flex-1" data-testid={`email-receipt-${booking.id}`}>
                    <Mail className="w-4 h-4 mr-2" /> Email
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          {!isPast && (
            <Button variant="destructive" size="sm" onClick={() => {
              if (!adminEmail) { toast.error("Email amministratore non configurata."); return; }
              const text = generateReceiptText(booking);
              window.location.href = `mailto:${adminEmail}?subject=${encodeURIComponent(`RICHIESTA CANCELLAZIONE - ${booking.id.substring(0, 8).toUpperCase()}`)}&body=${encodeURIComponent(`Richiesta di cancellazione:\n\n${text}`)}`;
              toast.success('Apertura app email...');
            }} data-testid={`cancel-booking-${booking.id}`}>
              <XCircle className="w-4 h-4 mr-2" /> Richiedi Cancellazione
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-background">
      <nav className="glass-nav sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <img src={LOGO_URL} alt="Comune di La Val" className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover" />
            <h1 className="font-heading font-extrabold text-lg sm:text-2xl tracking-tight">
              <span className="font-bold">Salf</span>
              <span className="font-light hidden sm:inline"> manifestaziuns</span>
            </h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <span className="text-xs sm:text-sm text-muted-foreground hidden md:inline" data-testid="user-name">{user?.nome_cognome}</span>
            {user?.is_admin && (
              <Button variant="outline" size="sm" onClick={() => navigate('/admin')} data-testid="admin-panel-button">
                <Shield className="w-4 h-4 mr-2" /> Admin
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="w-4 h-4 mr-2" /> Esci
            </Button>
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-4 py-6 sm:py-8">
        {announcement?.is_active && announcement?.message && (
          <div className="mb-6 sm:mb-8 p-4 rounded-lg border-2 border-red-500 bg-red-50 dark:bg-red-950/30" data-testid="announcement-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 sm:w-7 sm:h-7 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-800 dark:text-red-200 font-medium text-sm sm:text-base">{announcement.message}</p>
            </div>
          </div>
        )}

        <div className="mb-8 sm:mb-12 text-center space-y-3 sm:space-y-4">
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight">Prenota una Sala</h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto px-4">Scegli tra le nostre sale per le tue manifestazioni ed eventi</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {halls.map((hall) => (
            <Card key={hall.id} className="card-hover overflow-hidden" data-testid={`hall-card-${hall.id}`}>
              <div className="relative h-64 overflow-hidden">
                <img src={hall.image_url} alt={hall.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <h3 className="absolute bottom-4 left-4 font-heading font-bold text-3xl text-white">{hall.name}</h3>
              </div>
              <CardContent className="p-6 space-y-4">
                <p className="text-muted-foreground">{hall.description}</p>
                <div className="flex flex-wrap gap-2">
                  {hall.has_kitchen && <Badge variant="secondary" className="gap-1"><UtensilsCrossed className="w-3 h-3" /> Cucina</Badge>}
                  {hall.has_bar && <Badge variant="secondary" className="gap-1"><Wine className="w-3 h-3" /> Bar</Badge>}
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => navigate(`/calendar/${hall.id}`)} className="flex-1 btn-scale" data-testid={`view-calendar-${hall.id}`}>
                    <Calendar className="w-4 h-4 mr-2" /> Vedi Calendario
                  </Button>
                  <Button onClick={() => navigate(`/new-booking/${hall.id}`)} variant="outline" className="flex-1 btn-scale" data-testid={`new-booking-${hall.id}`}>Prenota</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Upcoming Bookings */}
        <div className="space-y-4 sm:space-y-6 mb-8">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-bold text-2xl sm:text-3xl" data-testid="upcoming-title">
              Le Mie Prenotazioni ({upcoming.length})
            </h3>
          </div>
          {upcoming.length === 0 ? (
            <Card><CardContent className="p-12 text-center"><Calendar className="w-16 h-16 mx-auto text-muted-foreground mb-4" /><p className="text-muted-foreground">Nessuna prenotazione futura</p></CardContent></Card>
          ) : (
            <div className="grid gap-4">
              {upcoming.map(b => <BookingCard key={b.id} booking={b} isPast={false} />)}
            </div>
          )}
        </div>

        {/* Past Bookings (History) */}
        {past.length > 0 && (
          <div className="space-y-4 sm:space-y-6">
            <Button variant="ghost" onClick={() => setShowHistory(!showHistory)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground" data-testid="toggle-history">
              <History className="w-5 h-5" />
              <span className="font-heading font-bold text-xl sm:text-2xl">Storico Prenotazioni ({past.length})</span>
              <ChevronRight className={`w-5 h-5 transition-transform ${showHistory ? 'rotate-90' : ''}`} />
            </Button>
            {showHistory && (
              <div className="grid gap-4">
                {past.map(b => <BookingCard key={b.id} booking={b} isPast={true} />)}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
